/**
 * Placement libre du visuel sur le vêtement, pour « Personnalise ton VERSION ».
 *
 * C'est le même outil que « Ton texte » — même vêtement, même échelle, mêmes
 * repères, même commande écrite en centimètres — avec un fichier à la place des
 * lettres. Le client envoie son visuel, le voit apparaître sur le coton, et le
 * pose où il veut.
 *
 * Quatre choses méritent d'être dites, parce qu'elles ne se voient pas :
 *
 * 1. Le fichier survit au changement de variante. Le thème re-rend toute sa
 *    section quand on change de taille ou de couleur, ce qui remplace le champ
 *    de fichier par un champ vide — le client aurait vu son visuel disparaître
 *    après l'avoir choisi. Le fichier est donc gardé dans le module et remis
 *    dans le nouveau champ.
 *
 * 2. Tous les fichiers ne se montrent pas. Un PDF ne s'affiche pas dans une
 *    balise `img` ; plutôt que de refuser le format ou de faire semblant, on
 *    montre un cadre à la bonne largeur et la commande dit que la hauteur suit
 *    les proportions du fichier.
 *
 * 3. Le détourage part des bords, pas de la couleur. Un logo posé sur un carré
 *    blanc s'imprimerait avec son carré : en DTF, ce qui n'est pas transparent
 *    est encré. On enlève donc le fond — mais en remplissant depuis les bords
 *    de proche en proche, et non en effaçant tout ce qui est blanc. Sans ça,
 *    le blanc des yeux d'un personnage ou d'un contre-poinçon de lettre
 *    disparaîtrait au milieu du motif.
 *
 * 4. Ce qui est détouré part vraiment à l'impression. Le fichier remis dans le
 *    champ est l'image traitée, pas l'originale : sinon l'aperçu montrerait un
 *    fond transparent et l'atelier imprimerait le carré.
 */

import { Placement, souvenir, boiteFormat } from '@version/placement';

const DEPART = { x: 0, y: 8, largeur: 21, face: 'devant' };

/** Ce que le navigateur sait afficher dans une balise `img`. */
const PREVISUALISABLE = /^image\/(png|jpeg|webp|gif|svg\+xml)$/;

/** Ce qu'on sait détourer : il faut pouvoir lire les pixels. */
const DETOURABLE = /^image\/(png|jpeg|webp)$/;

/** Au-delà, le remplissage prendrait plusieurs secondes dans l'onglet. */
const PIXELS_MAX = 40e6;

const FACES = ['devant', 'dos'];

/**
 * Un emplacement par face, hors du DOM : à l'abri du re-rendu de la section.
 *
 * `retenu` est une vue sur celui de la face affichée. Le détour par un Proxy
 * évite de réécrire la vingtaine d'endroits qui lisent `retenu.fichier` ou
 * `retenu.ratio` : ils parlent désormais, sans le savoir, de la face en cours.
 * C'est le seul endroit du fichier où cette indirection existe — partout
 * ailleurs, `retenu` se lit comme avant.
 */
const vide = () => ({ original: null, fichier: null, url: null, ratio: null, detoure: false });
const retenus = { devant: vide(), dos: vide() };
const memoires = { devant: null, dos: null };
let faceVue = 'devant';

const retenu = new Proxy({}, {
  get: (_, cle) => retenus[faceVue][cle],
  set: (_, cle, valeur) => { retenus[faceVue][cle] = valeur; return true; },
});

const poids = (octets) => (octets < 1024 * 1024
  ? `${Math.max(1, Math.round(octets / 1024))} Ko`
  : `${(octets / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`);

/**
 * Retire le fond d'une image, en partant de ses bords.
 *
 * On n'efface pas « tout ce qui ressemble au fond » : on part des pixels du
 * bord et on avance de proche en proche tant que la couleur reste dans la
 * tolérance. Une zone de la même couleur mais enfermée dans le motif — le blanc
 * d'un œil, l'intérieur d'un O — n'est jamais atteinte, donc jamais effacée.
 *
 * @param {HTMLImageElement} image
 * @param {number} tolerance - écart de couleur admis, 0 à 255
 * @returns {Promise<Blob|null>} un PNG à fond transparent
 */
async function detourer(image, tolerance) {
  const L = image.naturalWidth, H = image.naturalHeight;
  if (!L || !H || L * H > PIXELS_MAX) return null;

  const toile = document.createElement('canvas');
  toile.width = L; toile.height = H;
  const ctx = toile.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(image, 0, 0);
  const image_ = ctx.getImageData(0, 0, L, H);
  const px = image_.data;

  /* La couleur de référence est la médiane des quatre coins : un seul coin
     peut tomber sur un pixel isolé, et une moyenne se ferait tirer par lui. */
  const coins = [[0, 0], [L - 1, 0], [0, H - 1], [L - 1, H - 1]].map(([x, y]) => (y * L + x) * 4);
  const ref = [0, 1, 2].map((c) => {
    const v = coins.map((q) => px[q + c]).sort((a, b) => a - b);
    return (v[1] + v[2]) / 2;
  });

  const vu = new Uint8Array(L * H);
  const file = new Int32Array(L * H);
  let debut = 0, fin = 0;
  const pousser = (i) => { if (!vu[i]) { vu[i] = 1; file[fin++] = i; } };

  const proche = (i) => {
    const q = i * 4;
    return Math.abs(px[q] - ref[0]) <= tolerance
      && Math.abs(px[q + 1] - ref[1]) <= tolerance
      && Math.abs(px[q + 2] - ref[2]) <= tolerance;
  };

  for (let x = 0; x < L; x++) {
    if (proche(x)) pousser(x);
    const bas = (H - 1) * L + x;
    if (proche(bas)) pousser(bas);
  }
  for (let y = 0; y < H; y++) {
    const g = y * L, d = y * L + L - 1;
    if (proche(g)) pousser(g);
    if (proche(d)) pousser(d);
  }

  while (debut < fin) {
    const i = file[debut++];
    px[i * 4 + 3] = 0;
    const x = i % L, y = (i / L) | 0;
    if (x > 0 && !vu[i - 1] && proche(i - 1)) pousser(i - 1);
    if (x < L - 1 && !vu[i + 1] && proche(i + 1)) pousser(i + 1);
    if (y > 0 && !vu[i - L] && proche(i - L)) pousser(i - L);
    if (y < H - 1 && !vu[i + L] && proche(i + L)) pousser(i + L);
  }

  /* Un bord net laisse un liseré de la couleur du fond, très visible sur un
     t-shirt noir. On adoucit d'un pixel : les pixels encore opaques qui
     touchent du vide passent à mi-alpha. */
  const aEffacer = [];
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < L - 1; x++) {
      const i = y * L + x;
      if (px[i * 4 + 3] === 0) continue;
      if (px[(i - 1) * 4 + 3] === 0 || px[(i + 1) * 4 + 3] === 0
        || px[(i - L) * 4 + 3] === 0 || px[(i + L) * 4 + 3] === 0) aEffacer.push(i);
    }
  }
  for (const i of aEffacer) px[i * 4 + 3] = 110;

  ctx.putImageData(image_, 0, 0);
  return new Promise((r) => toile.toBlob(r, 'image/png'));
}

class VersionImagePreview extends HTMLElement {
  connectedCallback() {
    this.scene = this.querySelector('[data-version-visuel-scene]');
    this.zone = this.querySelector('[data-version-visuel-zone]');
    this.motif = this.querySelector('[data-version-visuel-motif]');
    this.relief = this.querySelector('[data-version-visuel-relief]');
    this.attente = this.querySelector('[data-version-visuel-attente]');
    this.champs = Object.fromEntries(
      FACES.map((f) => [f, this.querySelector(`[data-version-visuel-champ][data-face="${f}"]`)])
    );
    this.etiquette = this.querySelector('[data-version-visuel-etiquette]');
    this.statut = this.querySelector('[data-version-visuel-statut]');
    this.vider = this.querySelector('[data-version-visuel-vider]');
    this.lecture = this.querySelector('[data-version-visuel-lecture]');
    this.detourage = this.querySelector('[data-version-visuel-detourage]');
    this.tolerance = this.querySelector('[data-version-visuel-tolerance]');
    this.faces = [...this.querySelectorAll('[data-version-visuel-face-choix]')];
    this.reperes = this.querySelector('[data-version-visuel-reperes]');
    if (!this.scene || !this.zone || !this.champs.devant) return;

    for (const f of FACES) memoires[f] = souvenir(`visuel-${f}`, { ...DEPART, face: f });
    this.placement = new Placement({
      hote: this,
      scene: this.scene,
      zone: this.zone,
      sure: this.querySelector('[data-version-visuel-sure]'),
      memoire: this.memoire,
    });

    this.tailleMax = Number(this.dataset.tailleMax) || 20 * 1024 * 1024;
    this.rafraichir = this.rafraichir.bind(this);

    for (const f of FACES) {
      this.champs[f]?.addEventListener('change', () => {
        /* Un champ ne peut recevoir un fichier que lorsque sa face est à
           l'écran — l'étiquette ne pointe que sur celui-là — mais on se cale
           quand même dessus, plutôt que de supposer. */
        faceVue = f;
        this.#recevoir(this.champs[f].files?.[0] ?? null);
      });
    }
    this.vider?.addEventListener('click', () => this.#recevoir(null));
    this.detourage?.addEventListener('change', () => this.#appliquerDetourage());
    this.tolerance?.addEventListener('change', () => this.#appliquerDetourage());
    document.addEventListener('change', this.rafraichir);
    for (const bouton of this.faces) {
      bouton.addEventListener('click', () => {
        /* Changer de face, c'est changer d'emplacement : la mémoire, le
           fichier et le champ du formulaire basculent ensemble. */
        faceVue = bouton.dataset.face;
        this.placement.memoire = this.memoire;
        this.#montrerFichierCourant();
        this.rafraichir();
      });
    }
    this.placement.brancherReperes(this.reperes, () => this.rafraichir());
    this.placement.brancherDeplacement(this.rafraichir);

    this.#brancherDepot();
    this.observateur = new ResizeObserver(this.rafraichir);
    this.observateur.observe(this.scene);

    /* Le fichier choisi avant un changement de variante est remis dans le
       nouveau champ : sans cela, il aurait disparu sans un mot. */
    for (const f of FACES) if (retenus[f].fichier) this.#reprendre(f);
    this.#montrerFichierCourant();
    this.rafraichir();
  }

  /** Le champ de fichier de la face affichée. */
  get champ() { return this.champs[faceVue]; }

  /** La position et la taille de la face affichée. */
  get memoire() { return memoires[faceVue]; }

  /** Les propriétés de commande d'une face. */
  #sortiesDe(face) {
    const prendre = (nom) => this.querySelector(`[data-sortie="${nom}"][data-face="${face}"]`);
    return {
      position: prendre('position'),
      taille: prendre('taille'),
      fond: prendre('fond'),
    };
  }

  /**
   * Remet à l'écran ce qui appartient à la face affichée : l'étiquette du
   * bouton pointe sur son champ, le statut raconte son fichier, et l'état du
   * détourage est le sien.
   */
  #montrerFichierCourant() {
    if (this.etiquette && this.champs[faceVue]) {
      this.etiquette.htmlFor = this.champs[faceVue].id;
    }
    const r = retenus[faceVue];
    this.toggleAttribute('data-detourable', Boolean(r.original) && DETOURABLE.test(r.original.type));
    if (this.detourage) this.detourage.checked = r.detoure;
    this.#dire(
      r.fichier ? `${r.fichier.name} — ${poids(r.fichier.size)}${r.detoure ? ' · fond retiré' : ''}` : '',
      r.fichier ? 'ok' : null
    );
    this.#montrer();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.rafraichir);
    this.observateur?.disconnect();
  }

  /** Glisser-déposer sur le vêtement, en plus du clic sur le bouton. */
  #brancherDepot() {
    const cible = this.querySelector('[data-version-visuel-depot]') ?? this;
    for (const nom of ['dragenter', 'dragover']) {
      cible.addEventListener(nom, (event) => {
        event.preventDefault();
        this.dataset.survol = 'oui';
      });
    }
    for (const nom of ['dragleave', 'drop']) {
      cible.addEventListener(nom, () => delete this.dataset.survol);
    }
    cible.addEventListener('drop', (event) => {
      event.preventDefault();
      const fichier = event.dataTransfer?.files?.[0];
      if (fichier) this.#recevoir(fichier, true);
    });
  }

  /**
   * Accepte — ou refuse — le fichier, et le montre.
   * @param {File|null} fichier
   * @param {boolean} [depose] - vrai s'il arrive par glisser-déposer, auquel cas
   *   il faut aussi le poser dans le champ, sinon il ne partirait pas au panier.
   */
  async #recevoir(fichier, depose = false) {
    if (fichier && fichier.size > this.tailleMax) {
      this.#dire(`Ce fichier fait ${poids(fichier.size)}, la limite est à ${poids(this.tailleMax)}.`, 'error');
      this.champ.value = '';
      return;
    }

    retenu.original = fichier;
    this.toggleAttribute('data-detourable', Boolean(fichier) && DETOURABLE.test(fichier.type));
    if (this.detourage && (!fichier || !DETOURABLE.test(fichier.type))) this.detourage.checked = false;

    if (!fichier) {
      this.#poser(null);
      this.champ.value = '';
      this.#dire('', null);
      return;
    }
    if (depose) this.#poserDansLeChamp(fichier);
    await this.#appliquerDetourage();
  }

  /** Recalcule le fichier effectif — détouré ou non — et le montre. */
  async #appliquerDetourage() {
    const fichier = retenu.original;
    if (!fichier) return;

    const veut = Boolean(this.detourage?.checked) && DETOURABLE.test(fichier.type);
    if (!veut) {
      this.#poser(fichier, false);
      this.#dire(`${fichier.name} — ${poids(fichier.size)}`, 'ok');
      return;
    }

    this.#dire('Détourage en cours…', 'ok');
    this.dataset.travaille = 'oui';
    try {
      const url = URL.createObjectURL(fichier);
      const sonde = await new Promise((ok, non) => {
        const im = new Image();
        im.addEventListener('load', () => ok(im));
        im.addEventListener('error', non);
        im.src = url;
      });
      const blob = await detourer(sonde, Number(this.tolerance?.value) || 32);
      URL.revokeObjectURL(url);
      if (!blob) {
        this.#dire('Ce fichier est trop grand pour être détouré ici. Il part tel quel.', 'error');
        this.detourage.checked = false;
        this.#poser(fichier, false);
        return;
      }
      const nom = fichier.name.replace(/\.[^.]+$/, '') + '-detoure.png';
      const propre = new File([blob], nom, { type: 'image/png' });
      this.#poser(propre, true);
      this.#dire(`${nom} — ${poids(propre.size)} · fond retiré`, 'ok');
    } catch {
      this.#dire("Le détourage n'a pas abouti. Le fichier part tel quel.", 'error');
      if (this.detourage) this.detourage.checked = false;
      this.#poser(fichier, false);
    } finally {
      delete this.dataset.travaille;
    }
  }

  /** Installe le fichier effectif : aperçu, champ du formulaire, proportions. */
  #poser(fichier, detoure = false) {
    if (retenu.url) URL.revokeObjectURL(retenu.url);
    retenu.fichier = fichier;
    retenu.detoure = detoure;
    retenu.url = fichier ? URL.createObjectURL(fichier) : null;
    retenu.ratio = null;

    if (fichier) this.#poserDansLeChamp(fichier);
    else this.champ.value = '';

    if (!fichier || !PREVISUALISABLE.test(fichier.type)) {
      this.#montrer();
      this.rafraichir();
      return;
    }
    const sonde = new Image();
    sonde.addEventListener('load', () => {
      retenu.ratio = sonde.naturalWidth && sonde.naturalHeight
        ? sonde.naturalWidth / sonde.naturalHeight
        : 1;
      this.#montrer();
      this.rafraichir();
    });
    sonde.addEventListener('error', () => { this.#montrer(); this.rafraichir(); });
    sonde.src = retenu.url;
  }

  /** Remet dans le champ un fichier qui n'y est pas passé, ou qui en est sorti. */
  #poserDansLeChamp(fichier) {
    try {
      const transfert = new DataTransfer();
      transfert.items.add(fichier);
      this.champ.files = transfert.files;
    } catch {
      /* Navigateur qui refuse : le client repassera par le bouton. */
    }
  }

  /**
   * Après un re-rendu de la section : les champs sont neufs, les fichiers non.
   * On les remet tous les deux, pas seulement celui qu'on regarde — sinon
   * changer de variante perdrait silencieusement le visuel de l'autre face.
   */
  #reprendre(face) {
    const r = retenus[face];
    if (!r.fichier || !this.champs[face]) return;
    try {
      const transfert = new DataTransfer();
      transfert.items.add(r.fichier);
      this.champs[face].files = transfert.files;
    } catch {
      /* Navigateur qui refuse : le client repassera par le bouton. */
    }
  }

  #dire(texte, etat) {
    if (!this.statut) return;
    this.statut.textContent = texte;
    if (etat) this.statut.dataset.state = etat;
    else delete this.statut.dataset.state;
  }

  /** Accroche le visuel — ou son cadre d'attente — dans la scène. */
  #montrer() {
    const montrable = Boolean(retenu.url) && retenu.ratio !== null;
    this.toggleAttribute('data-fichier', Boolean(retenu.fichier));
    this.toggleAttribute('data-montrable', montrable);

    if (this.motif) {
      if (montrable) this.motif.src = retenu.url;
      else this.motif.removeAttribute('src');
    }
    if (this.relief) {
      this.relief.style.setProperty('--motif', montrable ? `url("${retenu.url}")` : 'none');
    }
    if (this.attente) {
      this.attente.dataset.message = retenu.fichier
        ? 'Aperçu impossible pour ce format — la hauteur suivra les proportions de ton fichier.'
        : '';
    }
  }

  /**
   * La largeur du motif, déduite du format choisi.
   *
   * Le motif remplit la boîte du format. Un visuel très haut est rabaissé pour
   * qu'il tienne entre le col et l'ourlet : c'est la seule chose qui puisse le
   * réduire en deçà du format, et elle est physique, pas tarifaire.
   */
  #largeurPour(ratio) {
    const boite = boiteFormat();
    const { g, scene, pxParCm } = this.placement.echelle();
    const utile = pxParCm ? this.placement.hauteurUtile(g, scene, pxParCm) : boite.hauteur;
    const hauteurPossible = Math.min(boite.hauteur, utile);
    if (!ratio) return boite.largeur;
    return Math.max(6, Math.min(boite.largeur, Math.round(hauteurPossible * ratio)));
  }

  /**
   * Fait suivre le format — donc le prix — à la taille du motif.
   *
   * Entre deux faces, c'est la plus grande qui décide : un transfert A4 ne se
   * débite pas en deux A5, et on ne facture pas deux fois le même achat de
   * feuille. Le second emplacement se paie à part.
   */
  rafraichir() {
    this.dataset.vetement = this.placement.teinte;
    this.dataset.face = this.memoire.face;
    for (const bouton of this.faces) {
      bouton.setAttribute('aria-pressed', String(bouton.dataset.face === this.memoire.face));
    }
    this.placement.filtrerReperes(this.reperes);

    /* Le format commande seul : on relit la largeur à chaque rendu plutôt que
       de la garder en mémoire, sinon un format changé laisserait l'ancienne
       taille à l'écran. */
    const ratio = retenu.ratio;
    this.memoire.largeur = this.#largeurPour(ratio);

    this.zone.style.setProperty('--ratio', String(ratio || 1));

    /* Sans proportions connues, on garde le motif dans le cadre comme s'il était
       carré : c'est le pire cas, donc le plus sûr. */
    const hauteurCm = ratio ? this.memoire.largeur / ratio : null;
    this.placement.caler(hauteurCm ?? this.memoire.largeur);
    if (!this.placement.poser()) return;
    this.#calerRelief();

    if (this.lecture) this.lecture.textContent = this.#lectureComplete();
    for (const f of FACES) this.#noterFace(f);
  }

  /**
   * Les propriétés d'une face, écrites même quand elle n'est pas à l'écran :
   * le client place son dos, revient au devant, et commande. Ce qui n'est plus
   * affiché doit quand même partir avec la commande.
   *
   * Une face sans fichier voit ses champs désactivés — un champ désactivé n'est
   * pas envoyé —, pour que la commande ne porte pas un « Dos — position » vide
   * qui laisserait croire à un second emplacement.
   */
  #noterFace(face) {
    const sorties = this.#sortiesDe(face);
    const r = retenus[face];
    const m = memoires[face];
    const actif = Boolean(r.fichier);
    for (const champ of Object.values(sorties)) if (champ) champ.disabled = !actif;
    if (!actif || !m) return;

    m.largeur = this.#largeurPour(r.ratio);

    const hauteurCm = r.ratio ? m.largeur / r.ratio : null;
    /* `noter` lit la mémoire du placement : on la lui prête le temps d'écrire
       celle d'une autre face, puis on la rend. */
    const garde = this.placement.memoire;
    this.placement.memoire = m;
    this.placement.noter(sorties, hauteurCm);
    this.placement.memoire = garde;
    if (sorties.fond) sorties.fond.value = r.detoure ? 'Détouré — fond retiré' : 'Tel quel';
  }

  /** La lecture sous l'aperçu : la face regardée, et l'autre si elle porte un visuel. */
  #lectureComplete() {
    const ici = this.placement.lecture();
    const autre = faceVue === 'devant' ? 'dos' : 'devant';
    if (!retenus[autre].fichier) return ici;
    return `${ici} · ${autre === 'dos' ? 'dos' : 'devant'} : un second visuel est posé`;
  }

  /**
   * Aligne la carte de relief sur la photo.
   * Le calque couvre exactement le motif, mais son fond doit rester calé sur le
   * vêtement : sans ce décalage, la trame du coton se déplacerait avec le
   * visuel, et l'illusion tomberait au premier mouvement.
   */
  #calerRelief() {
    if (!this.relief) return;
    const scene = this.scene.getBoundingClientRect();
    const zone = this.zone.getBoundingClientRect();
    if (!scene.width) return;
    this.relief.style.setProperty('--relief-taille', `${scene.width}px ${scene.height}px`);
    this.relief.style.setProperty('--relief-pos', `${scene.left - zone.left}px ${scene.top - zone.top}px`);
  }
}

if (!customElements.get('version-image-preview')) {
  customElements.define('version-image-preview', VersionImagePreview);
}
